import { FC } from 'react';

import { Paper, PaperProps, Text } from '@mantine/core';

/**
 * InfoMessage Component
 *
 * A reusable component to display informational messages, such as "No Data" or error fallbacks.
 * It extends Mantine's `Paper` component, allowing additional props to be passed down.
 *
 * @param {string} title - The title of the message.
 * @param {string} message - The description or details of the message.
 * @param {PaperProps} rest - Additional props passed to the underlying `Paper` component.
 *
 * @example
 * // Basic usage:
 * <InfoMessage
 *   title="No Data Available"
 *   message="No data is currently available. Try adjusting your filters."
 * />
 *
 * @example
 * // Using extended Paper props:
 * <InfoMessage
 *   title="Error"
 *   message="Something went wrong. Please try again later."
 *   shadow="sm"
 *   radius="md"
 * />
 */
interface InfoMessageProps extends PaperProps {
  title: string;
  message: string;
}

const InfoMessage: FC<InfoMessageProps> = ({ title, message, ...rest }) => (
  <Paper p="xl" withBorder ta={'center'} {...rest}>
    <Text size="lg" fw={700} c="gray.7" my="xs">
      {title}
    </Text>
    <Text size="sm" c="dimmed" mb="lg">
      {message}
    </Text>
  </Paper>
);

export default InfoMessage;
